// White-label-safe backdrop glow + focus-ring helpers for the auth screens.
//
// The glow and focus ring derive their colour from `theme.palette.primary.main`
// (= the brand `buttonStyle` the client explicitly chose). We deliberately do
// NOT read `theme.accent`/`theme.header`: those default to TrakGene teal
// (#2ba9bf) when a white-label client leaves them unset, which would leak
// TrakGene identity behind an un-branded client's card.
//
// Safety guards (per the redesign plan):
//   - If the brand colour is missing/invalid or near-white (a light brand),
//     fall back to a NEUTRAL grey glow (not a brand colour) so the glow stays
//     legible and never disappears or leaks teal.
//   - Opacity is mode-aware and capped for very dark brand colours so the glow
//     reads as light rather than a smudge.
import { alpha } from '@mui/material/styles';

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

// Neutral (non-brand) fallback glow colours — soft slate. Light in dark mode so
// the glow shows against a dark backdrop; a gentle grey in light mode.
const NEUTRAL_GLOW = { dark: '#cbd5e1', light: '#94a3b8' };

function hexToRgb(hex) {
  if (typeof hex !== 'string' || !HEX_RE.test(hex.trim())) return null;
  let h = hex.trim().slice(1);
  if (h.length === 3)
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  const int = parseInt(h, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

// WCAG relative luminance (0 = black, 1 = white).
export function relativeLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const lin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(rgb.r) + 0.7152 * lin(rgb.g) + 0.0722 * lin(rgb.b);
}

// Resolve the glow source colour + opacity for the current theme.
export function resolveGlow(theme) {
  const mode = theme?.palette?.mode === 'dark' ? 'dark' : 'light';
  const primary = theme?.palette?.primary?.main;
  const lum = relativeLuminance(primary);

  // Unusable (missing/invalid) or near-white brand → neutral fallback.
  if (lum === null || lum > 0.85) {
    return { color: NEUTRAL_GLOW[mode], opacity: mode === 'dark' ? 0.2 : 0.1 };
  }

  // Usable brand colour. Brighter glow in dark mode; softer in light mode.
  let opacity = mode === 'dark' ? 0.22 : 0.14;
  // A very dark brand on a light backdrop reads as a dirty smudge — soften it.
  if (mode === 'light' && lum < 0.08) opacity = 0.09;
  return { color: primary, opacity };
}

// sx for the full-viewport backdrop: theme background base + a soft radial glow
// near the top-centre. Mode-aware and fully theme-driven.
export function authGlowSx(theme) {
  const { color, opacity } = resolveGlow(theme);
  return {
    backgroundColor: theme?.palette?.background?.default,
    backgroundImage: `radial-gradient(120% 75% at 50% 0%, ${alpha(
      color,
      opacity
    )} 0%, ${alpha(color, 0)} 60%)`
  };
}

// Focus-ring box-shadow using the same source colour, so inputs/buttons focus
// states stay coherent with the glow and follow the brand.
export function authFocusRing(theme) {
  const primary = theme?.palette?.primary?.main;
  const ring = relativeLuminance(primary) === null ? '#42a5f5' : primary;
  return `0 0 0 3px ${alpha(ring, 0.4)}`;
}
